<template>
  <section class="c dashboard parking-lot-map">
    <resize-observer @notify="onResize"/>
    <v-layout row wrap fill-height>
      <v-flex xs12 md6 lg8 :class="{'full-mode': createMode}">
        <v-card class="map" ref="mapWrapper">
          <mapbox
            :access-token="mapSettings.accessToken"
            :map-style="mapSettings.style"
            :center="center"
            :zoom="mapSettings.zoom"
            :marker="parkingLotsMarkers"
            @marker-hover="onMarkerHover"
            @marker-click="onMarkerClick"
            @zoom="getMarkersInBounds"
            @drag="getMarkersInBounds"
            @zoom-start="setParkingListLoading(true)"
            @drag-start="setParkingListLoading(true)"
            @zoom-end="setParkingListLoading(false)"
            @drag-end="setParkingListLoading(false)"
            @load="onMapLoaded"
            @draw="onMapDraw"
            :draw-mode="createMode"
            v-if="showMap"
            ref="mapbox"
          ></mapbox>
          <parking-lot-creation
            ref="parkingLotCreation"
            :geo-json="creationGeoJson"
            @close="onParkingLotCreationClosed"
            @save="onParkingLotCreationSaved"
          ></parking-lot-creation>
        </v-card>
      </v-flex>
      <transition name="fadeDash">
        <v-flex xs12 md6 lg4 class="parking-lot-list-wrapper" v-show="!createMode">
          <v-btn round color="primary" large @click="openParkingLotCreation">
            <v-icon>add</v-icon>
            <span>Standort hinzufügen</span>
          </v-btn>
          <v-text-field
            label="Suche"
            v-model="searchList"
            box
            @input="searchListItems"
            clearable
          ></v-text-field>
          <parking-lot-list
            :data="allParkingLots"
            item-class-name="bc-list-item-bordered"
            two-line
            show-index
            :show-ids="markerIdsInBounds"
            :highlight-id="highlightId"
            :highlight-color="highlightColor"
            :loading="parkingLotListLoading"
            link="parking_lot"
            ref="parkingLotList"
            @item-hover="onParkingListItemHover"
            @load="setListHeight"/>
        </v-flex>
      </transition>
    </v-layout>
  </section>
</template>

<script>
import * as turf from '@turf/turf';
import ParkingLotList from '../ParkingLot/ParkingLotList.vue';
import ParkingLotCreation from '../ParkingLot/ParkingLotCreation.vue';

const Mapbox = () => import(/* webpackChunkName: "mapbox_dashboard" */ '../Mapbox.vue');

export default {
  name: 'parking-lot-map',

  components: {
    ParkingLotList,
    ParkingLotCreation,
    Mapbox,
  },

  data() {
    return {
      mapSettings: {
        zoom: 16,
        accessToken: this.$variables.mapbox.accessToken,
        style: this.$variables.mapbox.defaultOptions.style,
      },
      createMode: false,
      highlightId: null,
      highlightColor: null,
      creationGeoJson: null,
      markerIdsInBounds: [],
      parkingLotListLoading: true,
      listItemHovered: {
        newVal: null,
        oldVal: null,
      },
      showMap: true,
      searchList: '',
      allParkingLotsStorage: [],
    };
  },

  computed: {
    allParkingLots() {
      return this.$store.parkingLots.state.parkingLots;
    },
    parkingLotsMarkers() {
      const markers = [];
      this.$store.parkingLots.state.parkingLots.map((parkingLot) => {
        if (parkingLot.center) {
          markers.push(parkingLot.center);
        }
      });
      return markers;
    },
    center() {
      if (this.allParkingLots.length > 0) {
        var allMarkers = this.allParkingLots.map((parkingLot) => {
          return parkingLot.center.geoJson.geometry.coordinates;
        });
        var center = turf.center(turf.points(allMarkers));
        return center.geometry.coordinates;
      }
      return [10.014791, 53.548523];
    },
  },

  methods: {
    onParkingListItemClick(args) {
      this.$store.parkingLots.commit('SET_PARKING_LOT_DETAIL', args);
    },
    onMarkerHover(args) {
      this.highlightId = args.id;
      this.highlightColor = args.color;
    },
    onMarkerClick(id) {
      this.$router.push(`/parking_lot/${id}`);
    },
    openParkingLotCreation() {
      this.$emit('parking-lot-creation', true);
      this.createMode = true;
      this.$refs.parkingLotCreation.toggle();
      const interval = setInterval(() => {
        this.$refs.mapbox.map.resize();
      }, 1);
      setTimeout(() => {
        clearInterval(interval);
        this.$refs.mapbox.map.resize();
      }, 1000);
    },
    getMarkersInBounds() {
      this.markerIdsInBounds = this.$refs.mapbox.getMarkersInBounds();
    },
    setParkingListLoading(bool) {
      this.parkingLotListLoading = bool;
    },
    onMapLoaded() {
      this.parkingLotListLoading = false;
      this.getMarkersInBounds();
      this.fitMapToAllMarkers();
    },
    onParkingLotCreationClosed() {
      this.createMode = false;
      this.$emit('parking-lot-creation', false);
      const interval = setInterval(() => {
        if (this.$refs.mapbox) {
          this.$refs.mapbox.map.resize();
        }
      }, 1);
      setTimeout(() => {
        clearInterval(interval);
        this.$refs.mapbox.map.resize();
      }, 1000);
    },
    onMapDraw(e) {
      if (e.type === 'draw.create' || e.type === 'draw.update') {
        this.creationGeoJson = e.features[0].geometry;
      } else {
        this.creationGeoJson = null;
      }
    },
    fitMapToAllMarkers() {
      const allMarkers = [];
      this.allParkingLots.map((parkingLot, index) => {
        allMarkers.push(parkingLot.center.geoJson.geometry.coordinates);
      });

      if (allMarkers.length > 1) {
        const bbox = turf.bbox({ type: 'Polygon', coordinates: [allMarkers] });
        this.$refs.mapbox.map.fitBounds(bbox, { padding: 100 });
      }
    },
    onParkingListItemHover(args) {
      if (!this.parkingLotListLoading) {
        const oldVal = this.listItemHovered.oldVal;
        this.listItemHovered.newVal = args;
        if (this.listItemHovered.newVal !== null) {
          const el = this.$refs.mapbox.$el.querySelector(`#marker-${args}`);
          if (el) {
            el.classList.add('on-map-focus');
          }
        } else if (this.listItemHovered.newVal === null && oldVal !== null) {
          const el = this.$refs.mapbox.$el.querySelector(`#marker-${oldVal}`);
          if (el) {
            el.classList.remove('on-map-focus');
          }
        }
        this.listItemHovered.oldVal = args;
      }
    },
    onParkingLotCreationSaved(args) {
      this.$refs.mapbox.isLoading = true;
      this.showMap = false;
      this.$store.parkingLots.dispatch('POST_PARKING_LOT', {
        name: args.name,
        tag_id: args.tag_id,
        shape: args.shape,
      });
      setTimeout(() => {
        this.showMap = true;
      }, 500);
      setTimeout(() => {
        this.$refs.mapbox.isLoading = false;
      }, 750);
    },
    setListHeight() {
      this.$refs.parkingLotList.$el.style = `
        max-height: ${this.$refs.mapWrapper.$el.offsetHeight - 60 - 78}px;
        height: 100%;
        overflow: auto;
      `;
    },
    onResize() {
      this.setListHeight();
    },
    searchListItems() {
      const arr = [];
      if (this.allParkingLotsStorage.length === 0) {
        this.$store.parkingLots.state.parkingLots.map((parkingLot) => {
          this.allParkingLotsStorage.push(parkingLot);
        });
      }
      this.$store.parkingLots.state.parkingLots = this.allParkingLotsStorage;
      this.$store.parkingLots.state.parkingLots.map((parkingLot) => {
        const name = parkingLot.name.toLowerCase();
        const search = this.searchList.toLowerCase();
        if (name.indexOf(search) !== -1) {
          arr.push(parkingLot);
        }
      });
      this.$store.parkingLots.state.parkingLots = arr;
      this.getMarkersInBounds();
      if (arr.length >= 1) {
        this.fitMapToAllMarkers();
      }
    },
  },

  mounted() {
    this.$store.parkingLots.dispatch('GET_ALL_PARKING_LOTS');
  },
};
</script>

<style>
.fadeDash-enter-active, .fadeDash-leave-active {
  transition: transform 300ms;
  position: fixed;
  right: 0;
  width: 33.333%;
}
.fadeDash-enter, .fadeDash-leave-to /* .fade-leave-active below version 2.1.8 */ {
  transform: translateX(100%);
  position: fixed;
  right: 0;
}
</style>
